import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type VenueKpis = {
  totals: {
    posted: number;
    open: number;
    awaiting_payment: number;
    paid: number;
    filled: number;
    completed: number;
    cancelled: number;
    disputed: number;
  };
  spend: {
    total_pence: number;
    upcoming_committed_pence: number;
    avg_per_shift_pence: number;
    avg_hourly_rate: number | null;
    total_hours: number;
  };
  performance: {
    fill_rate: number;
    cancellation_rate: number;
    avg_hours_to_fill: number | null;
    dbs_share: number;
    auto_confirmed_count: number;
  };
  bartenders: {
    unique_count: number;
    repeat_count: number;
    favorites_count: number;
    avg_rating_given: number | null;
    avg_rating_received: number | null;
    top: { bartender_id: string; full_name: string | null; shifts: number; spend_pence: number; avg_rating: number | null }[];
  };
  monthly: { month: string; completed: number; spend_pence: number; hours: number }[];
};

export const getVenueKpis = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<VenueKpis> => {
    const { supabase, userId } = context;

    // Venue-only gate
    const { data: roleRow } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "venue")
      .maybeSingle();
    if (!roleRow) throw new Error("Venue access required.");


    const { data: shiftsRaw } = await supabase
      .from("shifts")
      .select("id,status,start_time,end_time,hourly_rate,total_cost,requires_dbs,bartender_id,created_at,completed_at,cancelled_at,auto_confirmed,payment_status")
      .eq("venue_id", userId);
    const shifts = shiftsRaw ?? [];

    const totals = {
      posted: shifts.length,
      open: 0, awaiting_payment: 0, paid: 0, filled: 0,
      completed: 0, cancelled: 0, disputed: 0,
    };
    let totalSpendPence = 0;
    let upcomingCommittedPence = 0;
    let completedHours = 0;
    let completedSpendPence = 0;
    let rateSum = 0;
    let rateCount = 0;
    let dbsCount = 0;
    let autoConfirmed = 0;
    const now = Date.now();
    const monthMap: Record<string, { completed: number; spend_pence: number; hours: number }> = {};
    const perBartender: Record<string, { shifts: number; spend_pence: number }> = {};
    const bartenderIds = new Set<string>();

    for (const s of shifts) {
      const status = s.status as keyof typeof totals;
      if (status in totals) totals[status]++;
      const rate = Number(s.hourly_rate) || 0;
      rateSum += rate; rateCount++;
      if (s.requires_dbs) dbsCount++;
      if (s.auto_confirmed) autoConfirmed++;
      const totalCostPence = Math.round(Number(s.total_cost ?? 0) * 100);
      const hours = (new Date(s.end_time).getTime() - new Date(s.start_time).getTime()) / 3_600_000;

      if (["paid", "filled", "completed"].includes(s.status)) {
        totalSpendPence += totalCostPence;
        if (new Date(s.end_time).getTime() > now && s.status !== "completed") {
          upcomingCommittedPence += totalCostPence;
        }
      }
      if (s.status === "completed") {
        completedHours += hours;
        completedSpendPence += totalCostPence;
        const m = (s.completed_at ?? s.end_time).slice(0, 7);
        if (!monthMap[m]) monthMap[m] = { completed: 0, spend_pence: 0, hours: 0 };
        monthMap[m].completed++;
        monthMap[m].spend_pence += totalCostPence;
        monthMap[m].hours += hours;
      }
      if (s.bartender_id) {
        bartenderIds.add(s.bartender_id);
        if (!perBartender[s.bartender_id]) perBartender[s.bartender_id] = { shifts: 0, spend_pence: 0 };
        if (s.status === "completed") {
          perBartender[s.bartender_id].shifts++;
          perBartender[s.bartender_id].spend_pence += totalCostPence;
        }
      }
    }

    // Avg hours to fill — based on bookings accepted_at proxy via earliest booking row updated_at
    const filledShiftIds = shifts
      .filter((s) => s.bartender_id && ["paid", "filled", "completed"].includes(s.status))
      .map((s) => s.id);
    let avgHoursToFill: number | null = null;
    if (filledShiftIds.length) {
      const { data: bookings } = await supabase
        .from("bookings")
        .select("shift_id,bartender_id,status,created_at")
        .in("shift_id", filledShiftIds)
        .eq("status", "accepted");
      const shiftCreated: Record<string, number> = {};
      for (const s of shifts) shiftCreated[s.id] = new Date(s.created_at).getTime();
      const diffs: number[] = [];
      for (const b of bookings ?? []) {
        const c = shiftCreated[b.shift_id];
        if (!c) continue;
        diffs.push((new Date(b.created_at).getTime() - c) / 3_600_000);
      }
      if (diffs.length) avgHoursToFill = Math.round((diffs.reduce((a, b) => a + b, 0) / diffs.length) * 10) / 10;
    }

    // Reviews — given by venue (reviewer = userId) and received about bartenders
    const { data: givenReviews } = await supabase
      .from("reviews")
      .select("rating")
      .eq("venue_id", userId);
    const avgGiven = givenReviews && givenReviews.length
      ? Math.round((givenReviews.reduce((a, r) => a + Number(r.rating), 0) / givenReviews.length) * 10) / 10
      : null;

    const bartenderIdList = Array.from(bartenderIds);
    let avgReceived: number | null = null;
    const ratingByBartender: Record<string, number | null> = {};
    if (bartenderIdList.length) {
      const { data: recv } = await supabase
        .from("reviews")
        .select("bartender_id,rating")
        .in("bartender_id", bartenderIdList);
      const groups: Record<string, number[]> = {};
      for (const r of recv ?? []) {
        if (!groups[r.bartender_id]) groups[r.bartender_id] = [];
        groups[r.bartender_id].push(Number(r.rating));
      }
      const allRatings: number[] = [];
      for (const [id, arr] of Object.entries(groups)) {
        const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
        ratingByBartender[id] = Math.round(avg * 10) / 10;
        allRatings.push(...arr);
      }
      if (allRatings.length) avgReceived = Math.round((allRatings.reduce((a, b) => a + b, 0) / allRatings.length) * 10) / 10;
    }

    // Bartender names
    const { data: profs } = bartenderIdList.length
      ? await supabase.from("profiles").select("id,full_name").in("id", bartenderIdList)
      : { data: [] };
    const nameMap: Record<string, string | null> = {};
    for (const p of profs ?? []) nameMap[p.id] = p.full_name;

    const top = Object.entries(perBartender)
      .map(([id, v]) => ({ bartender_id: id, full_name: nameMap[id] ?? null, shifts: v.shifts, spend_pence: v.spend_pence, avg_rating: ratingByBartender[id] ?? null }))
      .filter((x) => x.shifts > 0)
      .sort((a, b) => b.shifts - a.shifts)
      .slice(0, 5);

    const repeatCount = Object.values(perBartender).filter((v) => v.shifts >= 2).length;

    const { count: favCount } = await supabase
      .from("favorite_bartenders")
      .select("*", { count: "exact", head: true })
      .eq("venue_id", userId);

    const decided = totals.completed + totals.cancelled + totals.filled + totals.paid;
    const fillRate = totals.posted ? (totals.filled + totals.paid + totals.completed) / totals.posted : 0;
    const cancelRate = totals.posted ? totals.cancelled / totals.posted : 0;

    const monthly = Object.entries(monthMap)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .slice(-6)
      .map(([month, v]) => ({ month, completed: v.completed, spend_pence: v.spend_pence, hours: Math.round(v.hours * 10) / 10 }));

    return {
      totals,
      spend: {
        total_pence: totalSpendPence,
        upcoming_committed_pence: upcomingCommittedPence,
        avg_per_shift_pence: totals.completed ? Math.round(completedSpendPence / totals.completed) : 0,
        avg_hourly_rate: rateCount ? Math.round((rateSum / rateCount) * 100) / 100 : null,
        total_hours: Math.round(completedHours * 10) / 10,
      },
      performance: {
        fill_rate: Math.round(fillRate * 1000) / 10,
        cancellation_rate: Math.round(cancelRate * 1000) / 10,
        avg_hours_to_fill: avgHoursToFill,
        dbs_share: totals.posted ? Math.round((dbsCount / totals.posted) * 1000) / 10 : 0,
        auto_confirmed_count: autoConfirmed,
      },
      bartenders: {
        unique_count: bartenderIds.size,
        repeat_count: repeatCount,
        favorites_count: favCount ?? 0,
        avg_rating_given: avgGiven,
        avg_rating_received: avgReceived,
        top,
      },
      monthly,
    };
    void decided;
  });
